// Mission presentation: Current Mission card, Mission Map and domain journey strip.
// READ-ONLY by design: these helpers only call progress getters (domainSummary,
// unitState, canOpen, currentUnit, phase2Unlocked) and never write progress,
// lab state or storage. Visual state is derived from progress, never the reverse.

import { h } from './h.js';
import { curriculum } from '../core/curriculum.js';
import { cityById } from '../map/mapData.js';

// Mission sites come from the Philippine mission-world data (src/map/mapData.js).
const siteOf = (dId) => { const c = cityById(dId); return c ? `${c.name} · ${c.site.name}` : '—'; };

const KIND_LABEL = { switch: 'switch', router: 'router', pc: 'endpoint', server: 'endpoint', cloud: 'cloud' };

/** Domain-level visual state from progress (complete | current | pending | locked). */
export function domainVisualState(s) {
  if (s.complete) return 'complete';
  if (!s.unlocked) return 'locked';
  if (s.pending === s.total) return 'pending';
  return 'current';
}

function unitVisualState(st) {
  return st === 'complete' ? 'complete' : st === 'available' || st === 'in-progress' ? 'current' : st === 'pending' ? 'pending' : 'locked';
}

const capstoneOf = (d) => d.units.find((u) => /final/i.test(u.type)) || d.units[d.units.length - 1];
const cleanTitle = (t) => t.replace(/^FINAL CHALLENGE\s*[—–-]\s*/i, '');

function scaleOf(u) {
  if (!u?.implemented || typeof u.network !== 'function') return '—';
  try {
    const counts = {};
    for (const dev of Object.values(u.network().devices || {})) {
      const k = KIND_LABEL[dev.kind] || dev.kind;
      counts[k] = (counts[k] || 0) + 1;
    }
    const order = ['switch', 'router', 'endpoint', 'cloud'];
    return Object.keys(counts).sort((a, b) => order.indexOf(a) - order.indexOf(b))
      .map((k) => `${counts[k]} ${k}${counts[k] > 1 ? (k === 'switch' ? 'es' : 's') : ''}`).join(' · ') || '—';
  } catch { return '—'; }
}

function faultsOf(u) {
  if (!u?.implemented || !u.faultCatalog) return null;
  return new Set(Object.values(u.faultCatalog).map((f) => f.group)).size;
}

/** Mission model for one domain — derived data only. */
export function missionModel(progress, d) {
  const s = progress.domainSummary(d.id);
  const cap = capstoneOf(d);
  const next = progress.currentUnit(d.id);
  const idx = next ? d.units.indexOf(next) : -1;
  return {
    d, s, cap, next, idx,
    state: domainVisualState(s),
    title: cleanTitle(cap.title),
    description: cap.implemented ? cap.objective : `${d.tagline} This mission is still being built.`,
    site: siteOf(d.id),
    scale: scaleOf(cap),
    faults: faultsOf(cap),
  };
}

function fact(label, value, cls = '') {
  return h('div', { class: `mission-fact ${cls}` }, h('span', { class: 'mf-label' }, label), h('span', { class: 'mf-value' }, value));
}

/** Small strip of unit nodes for a domain (used on the mission card and domain header). */
export function journeyStrip(progress, d, { links = false } = {}) {
  return h('ol', { class: 'journey', 'aria-label': `Domain ${d.number} journey` },
    d.units.map((u, i) => {
      const st = progress.unitState(d.id, u.id);
      const vs = unitVisualState(st);
      const isFinal = /final/i.test(u.type);
      const label = `${String(i + 1).padStart(2, '0')} · ${u.title} — ${st.replace('-', ' ')}`;
      const inner = h('span', { class: 'jn-dot' }, vs === 'complete' ? '✓' : isFinal ? '★' : String(i + 1));
      const open = links && progress.canOpen(d.id, u.id);
      return h('li', { class: `jn status-${vs} ${vs} ${isFinal ? 'final' : ''}`, title: label },
        open ? h('a', { href: `#/unit/${d.id}/${u.id}`, 'aria-label': label, tabindex: '-1' }, inner) : h('span', { 'aria-label': label }, inner));
    }));
}

/** CURRENT MISSION card (dark). */
export function currentMissionCard(progress) {
  const pick = curriculum.domains.find((d) => { const s = progress.domainSummary(d.id); return s.unlocked && !s.complete; });
  if (!pick) {
    return h('section', { class: 'mission-card panel-dark', 'data-testid': 'current-mission' },
      h('div', { class: 'mission-eyebrow' }, 'CURRENT MISSION'),
      h('h2', { class: 'mission-title' }, 'All available missions complete'),
      h('p', { class: 'mission-desc' }, 'Every unlocked domain is complete. New missions appear here as they are built.'));
  }
  const m = missionModel(progress, pick);
  const started = m.s.done > 0 || (m.next && progress.unitState(pick.id, m.next.id) === 'in-progress');
  const nextLine = m.next
    ? h('div', { class: 'mission-next' }, h('span', { class: 'mn-label' }, 'NEXT STEP'), h('span', {}, `Unit ${String(m.idx + 1).padStart(2, '0')} · ${m.next.title}`))
    : h('div', { class: 'mission-next pending' }, h('span', { class: 'mn-label' }, 'STATUS'), h('span', {}, 'IMPLEMENTATION PENDING — this mission cannot be started yet.'));
  return h('section', { class: `mission-card panel-dark mission-${m.state}`, 'data-testid': 'current-mission' },
    h('div', { class: 'mission-top' },
      h('div', { class: 'mission-eyebrow' }, 'CURRENT MISSION'),
      h('span', { class: 'mission-domain' }, `DOMAIN ${pick.number} · ${pick.title.toUpperCase()}`)),
    h('h2', { class: 'mission-title' }, m.title),
    h('p', { class: 'mission-desc' }, m.description),
    h('div', { class: 'mission-facts' },
      fact('SITE', m.site),
      fact('SCALE', m.scale),
      fact('FAULTS', m.faults ? `${m.faults} hidden` : '—', m.faults ? 'is-faults' : '')),
    h('div', { class: 'mission-journey' },
      h('div', { class: 'mj-head' }, h('span', {}, 'MISSION JOURNEY'), h('span', { class: 'mj-count' }, `${m.s.done}/${m.s.total} units`)),
      journeyStrip(progress, pick)),
    h('div', { class: 'mission-actions' },
      nextLine,
      h('div', { class: 'row mission-buttons' },
        h('a', { class: 'btn mission-mapbtn', href: m.next ? `#/map/${pick.id}/site/${m.next.id}` : `#/map/${pick.id}`, 'data-testid': 'view-map' }, '🗺 VIEW MISSION MAP'),
        m.next ? h('a', { class: 'btn primary mission-start', href: `#/unit/${pick.id}/${m.next.id}`, 'data-testid': 'start-mission' }, started ? 'CONTINUE MISSION →' : 'START MISSION →') : null)));
}

/** MISSION MAP: all domains + Phase 2 as a path of nodes (dark). */
export function missionMap(progress) {
  const p2 = progress.phase2Unlocked();
  const nodes = curriculum.domains.map((d) => {
    const s = progress.domainSummary(d.id);
    const vs = domainVisualState(s);
    const stateText = { complete: 'Complete', current: s.done ? `In progress · ${s.done}/${s.total}` : 'Ready to start', pending: 'Implementation pending', locked: 'Locked' }[vs];
    const body = [
      h('span', { class: 'mm-node' }, vs === 'complete' ? '✓' : vs === 'locked' ? '🔒' : d.number),
      h('span', { class: 'mm-text' },
        h('span', { class: 'mm-num' }, `DOMAIN ${d.number}`),
        h('span', { class: 'mm-title' }, d.title),
        h('span', { class: 'mm-state' }, stateText)),
    ];
    return h('li', { class: `mm-stop status-${vs === 'pending' ? 'locked' : vs} ${vs}` },
      s.unlocked ? h('a', { class: 'mm-link', href: `#/domain/${d.id}` }, body) : h('div', { class: 'mm-link', 'aria-disabled': 'true' }, body));
  });
  nodes.push(h('li', { class: `mm-stop phase2-stop ${p2 ? 'status-complete complete' : 'status-locked locked'}` },
    h('div', { class: 'mm-link' },
      h('span', { class: 'mm-node' }, p2 ? '★' : '🔒'),
      h('span', { class: 'mm-text' }, h('span', { class: 'mm-num' }, 'PHASE 2'), h('span', { class: 'mm-title' }, curriculum.phase2.title), h('span', { class: 'mm-state' }, p2 ? 'Unlocked' : 'After Domain 07')))));
  return h('section', { class: 'mission-map panel-dark', 'aria-label': 'Mission map' },
    h('div', { class: 'mm-head' },
      h('div', {}, h('div', { class: 'mission-eyebrow' }, 'MISSION MAP'), h('h2', {}, 'Your path to network engineer')),
      h('a', { class: 'btn small', href: '#/map', 'data-testid': 'open-map' }, 'Open the Philippines map →'),
      h('div', { class: 'mm-legend' },
        h('span', { class: 'status-complete' }, h('i'), 'Complete'),
        h('span', { class: 'status-current' }, h('i'), 'Current'),
        h('span', { class: 'status-locked' }, h('i'), 'Locked'))),
    h('ol', { class: 'mm-path' }, nodes));
}
